import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, setBlockTracking as _setBlockTracking, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = { class: "card" };
const _hoisted_2 = {
    key: 0,
    class: "px-3 lg:px-4 pt-3 pb-2"
};
const _hoisted_3 = { class: "flex justify-between items-center" };
const _hoisted_4 = { class: "font-semibold" };
const _hoisted_5 = ["aria-label", "title"];
const _hoisted_6 = { class: "my-1 text-muted collapsible-content" };
const _hoisted_7 = { class: "w-full relative aspect-4/3 sm:aspect-video lg:aspect-2/1" };
const _hoisted_8 = {
    key: 0,
    class: "absolute inset-0 flex justify-center items-center text-muted z-2"
};
import { computed, ref, watch } from 'vue';
import { ___, toFixedNumberStr } from '@ts/common/language';
import { useDeferredChart } from '@ts/common/useDeferredChart';
import { kcalToG } from '@ts/common/nutrition';
import SvgVue from '@ts/components/utils/SvgVue.vue';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import {} from '@ts/common/datasets';
import {} from 'chart.js/auto';
import {} from '@ts/common/chart';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ChartMetabolism',
    props: {
        data: { default: undefined },
        help: { type: Boolean, default: false },
        header: { type: Boolean, default: true },
        tooltip: { type: Boolean, default: true },
        overrideAnnotations: { type: Boolean, default: false },
        annotations: { default: [] }
    },
    setup(__props) {
        Chart.register(annotationPlugin);
        const props = __props;
        const showHelp = ref(false);
        const showPlaceholder = ref(true);
        const chart = ref(null);
        const containerRef = ref(null);
        // True only when the carb/fat range band is actually shown (calibrated model / v4.2).
        const bandAvailable = computed(() => {
            const cm = props.data?.chart_metabolism;
            return (props.data?.performance_computations_version === '2' &&
                Array.isArray(cm?.energy_carbo_per_hour_low) &&
                Array.isArray(cm?.energy_carbo_per_hour_high) &&
                Array.isArray(cm?.energy_fat_per_hour_low) &&
                Array.isArray(cm?.energy_fat_per_hour_high));
        });
        let chartObj = undefined;
        // Create the chart only once its container has a real (non-zero) size — see useDeferredChart.
        useDeferredChart(containerRef, {
            create: () => {
                if (props.data !== undefined)
                    drawChart(props.data);
            },
            getChart: () => chartObj,
        });
        watch(() => props.data, (newValue, oldValue) => {
            if (newValue !== undefined)
                drawChart(newValue);
        }, { deep: true });
        function drawChart(data) {
            if (chart.value === null)
                return;
            const cm = data.chart_metabolism;
            const toPoints = (arr) => arr.map((value, index) => ({ x: cm.power[index], y: value }));
            const bandAvailable = data.performance_computations_version === '2' &&
                Array.isArray(cm.energy_carbo_per_hour_low) &&
                Array.isArray(cm.energy_carbo_per_hour_high) &&
                Array.isArray(cm.energy_fat_per_hour_low) &&
                Array.isArray(cm.energy_fat_per_hour_high);
            // Middle (centre) lines — index 0 = fat (y1), index 1 = carbohydrates (y).
            const datasets = [
                {
                    data: toPoints(cm.energy_fat_per_hour),
                    label: ___({ en: 'Fat', de: 'Fett' }),
                    borderColor: '#2fb8c2',
                    pointRadius: 0,
                    showLine: true,
                    fill: false,
                    yAxisID: 'y1',
                },
                {
                    data: toPoints(cm.energy_carbo_per_hour),
                    label: ___({ en: 'Carbohydrates', de: 'Kohlenhydrate' }),
                    borderColor: '#cdb663',
                    pointRadius: 0,
                    showLine: true,
                    fill: false,
                    yAxisID: 'y',
                },
            ];
            // Range band (indices 2–5): faint low/high boundary lines with the high curve
            // filling down to its low curve (fill: '-1'). Visible thin borders both show the
            // range edges and give the Filler a concrete boundary to fill between.
            if (bandAvailable) {
                datasets.push({ data: toPoints(cm.energy_carbo_per_hour_low), borderColor: 'rgba(205, 182, 99, 0.5)', borderWidth: 1, backgroundColor: 'rgba(205, 182, 99, 0.20)', pointRadius: 0, showLine: true, fill: false, yAxisID: 'y' }, { data: toPoints(cm.energy_carbo_per_hour_high), borderColor: 'rgba(205, 182, 99, 0.5)', borderWidth: 1, backgroundColor: 'rgba(205, 182, 99, 0.20)', pointRadius: 0, showLine: true, fill: '-1', yAxisID: 'y' }, { data: toPoints(cm.energy_fat_per_hour_low), borderColor: 'rgba(47, 184, 194, 0.5)', borderWidth: 1, backgroundColor: 'rgba(47, 184, 194, 0.20)', pointRadius: 0, showLine: true, fill: false, yAxisID: 'y1' }, { data: toPoints(cm.energy_fat_per_hour_high), borderColor: 'rgba(47, 184, 194, 0.5)', borderWidth: 1, backgroundColor: 'rgba(47, 184, 194, 0.20)', pointRadius: 0, showLine: true, fill: '-1', yAxisID: 'y1' });
            }
            const chartData = {
                labels: cm.power.map((value) => value.toString()),
                datasets,
            };
            const chartOptions = {
                maintainAspectRatio: false,
                responsive: true,
                animation: false,
                events: props.tooltip ? ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'] : [],
                scales: {
                    x: {
                        // id: "x-axis-1",
                        title: {
                            display: true,
                            text: ___({
                                en: 'Power [W]',
                                de: 'Leistung [W]',
                            }),
                        },
                        // type: 'category',
                        type: 'linear',
                        max: Math.ceil(data.chart_metabolism.power_at_zero_fat / 10) * 10,
                        // max: data.chart_metabolism.power[data.chart_o2_lact.power.length - 1],
                        min: data.chart_metabolism.power[0],
                        ticks: {
                            autoSkip: true,
                        },
                    },
                    y: {
                        // primary axis — carbohydrates (the limiting/important fuel)
                        type: 'linear',
                        position: 'left',
                        title: {
                            display: true,
                            text: ___({
                                en: 'Energy consumption carbohydrates [kcal/h]',
                                de: 'Energieverbrauch Kohlenhydrate [kcal/h]',
                            }),
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                        max: Math.floor(data.chart_metabolism.energy_carbo_per_hour_plot_limit / 10) * 10,
                        min: 0,
                        ticks: {
                            autoSkip: true,
                        },
                    },
                    y1: {
                        // secondary axis — fat (autoscaled)
                        type: 'linear',
                        position: 'right',
                        title: {
                            display: true,
                            text: ___({
                                en: 'Energy consumption fat [kcal/h]',
                                de: 'Energieverbrauch Fett [kcal/h]',
                            }),
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                        min: 0,
                        ticks: {
                            autoSkip: true,
                        },
                    },
                },
                interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false,
                },
                plugins: {
                    legend: {
                        labels: {
                            filter: (item) => (item.datasetIndex ?? 0) < 2,
                        },
                    },
                    ...(props.tooltip
                        ? {
                            tooltip: {
                                filter: (item) => (item.datasetIndex ?? 0) < 2,
                                callbacks: {
                                    title: (context) => {
                                        return ___({
                                            en: 'At ' + context[0].label + ' W:',
                                            de: 'Bei ' + context[0].label + ' W:',
                                        });
                                    },
                                    label: (context) => {
                                        // dataset 0 = fat, dataset 1 = carbohydrates
                                        const kcal = context.raw.y;
                                        const substrate = context.datasetIndex === 1 ? 'carb' : 'fat';
                                        return context.dataset.label + ': ' + toFixedNumberStr(kcalToG(kcal, substrate), 1) + ' g/h' + ' ⇔ ' + toFixedNumberStr(kcal, 0) + ' kcal/h';
                                    },
                                    labelColor: (context) => {
                                        return {
                                            borderColor: 'rgba(0, 0, 0, 0)',
                                            backgroundColor: context.dataset.borderColor ?? 'rgba(0, 0, 0, 0)',
                                            borderWidth: 0,
                                        };
                                    },
                                },
                            },
                        }
                        : {
                            tooltip: {
                                enabled: false,
                            },
                        }),
                    annotation: {
                        common: {
                            drawTime: 'afterDatasetsDraw',
                        },
                        annotations: [
                            ...(props.overrideAnnotations
                                ? []
                                : [
                                    {
                                        // drawTime: "afterDatasetsDraw",
                                        id: 'hline',
                                        type: 'line',
                                        mode: 'vertical',
                                        scaleID: 'x',
                                        value: data.fatmax_abs,
                                        borderColor: '#2cb7c0',
                                        // borderDash: [4, 4],
                                        borderDash: [10, 5],
                                        borderDashOffset: 5,
                                        // borderWidth: 3,
                                        borderWidth: 2,
                                        label: {
                                            position: 'end',
                                            backgroundColor: '#2cb7c0',
                                            content: 'Fatmax ',
                                            display: true,
                                        },
                                    },
                                ]),
                            ...props.annotations,
                        ],
                    },
                },
            };
            if (chartObj === undefined) {
                chartObj = new Chart(chart.value, {
                    type: 'line',
                    data: chartData,
                    options: chartOptions,
                });
                showPlaceholder.value = false;
            }
            else {
                chartObj.data = chartData;
                chartObj.options = chartOptions;
                chartObj.update();
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (__props.header)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(___)({ en: 'Energy Consumption', de: 'Energieverbrauch' })), 1 /* TEXT */),
                            (__props.help)
                                ? (_openBlock(), _createElementBlock("button", {
                                    key: 0,
                                    onClick: _cache[1] || (_cache[1] = ($event) => (showHelp.value = !showHelp.value)),
                                    type: "button",
                                    class: "btn text-gray-350 dark:text-gray-450 hover:text-primary-500",
                                    "aria-label": _unref(___)({ en: 'Toggle help', de: 'Hilfe ein- und ausblenden' }),
                                    title: _unref(___)({ en: 'Help', de: 'Hilfe' })
                                }, [
                                    _cache[0] || (_setBlockTracking(-1, true),
                                        (_cache[0] = _createVNode(SvgVue, {
                                            class: "w-4 h-4",
                                            icon: "lucide/circle-question-mark"
                                        })).cacheIndex = 0,
                                        _setBlockTracking(1),
                                        _cache[0])
                                ], 8 /* PROPS */, _hoisted_5))
                                : _createCommentVNode("v-if", true)
                        ]),
                        (__props.help)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: _normalizeClass(["collapsible", showHelp.value ? 'expanded' : ''])
                            }, [
                                _createElementVNode("div", _hoisted_6, [
                                    _createTextVNode(_toDisplayString(_unref(___)({
                                        en: 'This graph shows the different composition of the energy carriers fats and carbohydrates over the course of the performance, as well as the maximum rate of fat combustion - Fatmax.',
                                        de: 'Diese Grafik zeigt die unterschiedliche Zusammensetzung der Energieträger Fette und Kohlenhydrate über den Verlauf der Leistung, sowie die maximale Fettverbrennung Fatmax.',
                                    })), 1 /* TEXT */),
                                    (bandAvailable.value)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            _createTextVNode(_toDisplayString(_unref(___)({
                                                en: 'The shaded band around each line shows the plausible range of fat and carbohydrate use: the exact split between the two fuels cannot be pinned to a single value, so the model reports a physiologically reasonable range around the central estimate.',
                                                de: 'Das schattierte Band um jede Linie zeigt den plausiblen Bereich des Fett- und Kohlenhydratverbrauchs: Die genaue Aufteilung zwischen den beiden Energieträgern lässt sich nicht auf einen einzelnen Wert festlegen, daher gibt das Modell einen physiologisch sinnvollen Bereich um den zentralen Schätzwert an.',
                                            })), 1 /* TEXT */)
                                        ], 64 /* STABLE_FRAGMENT */))
                                        : _createCommentVNode("v-if", true)
                                ])
                            ], 2 /* CLASS */))
                            : _createCommentVNode("v-if", true)
                    ]))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("div", {
                        class: "absolute inset-0",
                        ref_key: "containerRef",
                        ref: containerRef
                    }, [
                        _createElementVNode("canvas", {
                            class: "w-full h-full",
                            ref_key: "chart",
                            ref: chart
                        }, null, 512 /* NEED_PATCH */)
                    ], 512 /* NEED_PATCH */),
                    (showPlaceholder.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _createElementVNode("div", null, [
                                _cache[2] || (_setBlockTracking(-1, true),
                                    (_cache[2] = _createVNode(SvgVue, {
                                        class: "w-4 h-4",
                                        icon: "lucide/chart-line"
                                    })).cacheIndex = 2,
                                    _setBlockTracking(1),
                                    _cache[2]),
                                _createTextVNode(" " + _toDisplayString(_unref(___)({ en: 'loading...', de: 'lädt...' })), 1 /* TEXT */)
                            ])
                        ]))
                        : _createCommentVNode("v-if", true)
                ])
            ]));
        };
    }
});
